import { wsconnect, type NatsConnection } from "@nats-io/nats-core";
import {
  AckPolicy,
  DeliverPolicy,
  jetstream,
  jetstreamManager,
} from "@nats-io/jetstream";
import type { JetStreamClient } from "@nats-io/jetstream";

// --- defaults ----------------------------------------------------------------

export const DEFAULT_NATS_URL = "wss://streamer.traderverse.io";
export const DEFAULT_STREAM_NAME = "prices-stream";

/**
 * Dev-only NATS JWT from the untracked `.env.development.local` (`VITE_DEV_NATS_JWT`; development mode only).
 * No credential is committed here (SEC-40); production builds never read it.
 */
const DEV_NATS_JWT: string = (import.meta.env.VITE_DEV_NATS_JWT as string | undefined)?.trim() ?? "";

// --- types -------------------------------------------------------------------

export type PriceKlinePayload = {
  timestamp: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume?: number;
};

export type NatsLiveHandle = { close: () => Promise<void> };

export type ResolveNatsAuthTokenParams = {
  natsToken?: string;
  apiKey?: string;
};

export type SubscribeSymbolOptions = ResolveNatsAuthTokenParams & {
  natsUrl?: string;
  streamName?: string;
};

// --- auth --------------------------------------------------------------------

export function getDevNatsToken(): string {
  if (!import.meta.env.DEV) return "";
  return DEV_NATS_JWT;
}

function readAuthFromLocalStorage(): string {
  if (typeof localStorage === "undefined") return "";
  const a = localStorage.getItem("auth_token")?.trim();
  if (a) return a;
  return localStorage.getItem("token")?.trim() ?? "";
}

/** `natsToken` → `localStorage.auth_token` / `token` → dev JWT (dev builds only) → `apiKey` */
export function resolveNatsAuthToken(params: ResolveNatsAuthTokenParams): string {
  const fromConfig = params.natsToken?.trim();
  if (fromConfig) return fromConfig;
  const fromStorage = readAuthFromLocalStorage();
  if (fromStorage) return fromStorage;
  const dev = getDevNatsToken();
  if (dev) return dev;
  return params.apiKey?.trim() ?? "";
}

// --- subject / helpers -------------------------------------------------------

/**
 * Rule 24 (14 Oct 2026): flat exact subjects `prices.{tvId}` for every asset class
 * (equity, crypto, forex, commodity). The legacy `prices.{assetClass}.{tvId}` mirror
 * is not to be built on.
 */
export function priceSubject(symbol: string): string {
  return `prices.${symbol}`;
}

export function lastPriceFromKline(p: PriceKlinePayload): number {
  return p.close;
}

// --- connection --------------------------------------------------------------

export async function connectNatsJetStream(
  natsUrl: string,
  token: string
): Promise<{ nc: NatsConnection; js: JetStreamClient }> {
  // Server INFO often lists internal IPs in connect_urls; browsers must stay on the public WSS URL.
  const nc = await wsconnect({
    servers: [natsUrl],
    token: token.trim(),
    ignoreClusterUpdates: true,
  });
  return { nc, js: jetstream(nc) };
}

export async function drainNats(nc: NatsConnection | null): Promise<void> {
  if (!nc) return;
  try {
    await nc.drain();
  } catch {
    try {
      await nc.close();
    } catch {
      /* ignore */
    }
  }
}

// --- connection pool (one WS per url+token) ----------------------------------

type PoolEntry = {
  ref: number;
  promise: Promise<{ nc: NatsConnection; js: JetStreamClient }>;
};

const pool = new Map<string, PoolEntry>();

function poolKey(natsUrl: string, token: string): string {
  return `${natsUrl}\0${token}`;
}

export async function acquireGlobalNatsJetStream(
  natsUrl: string,
  token: string
): Promise<{ nc: NatsConnection; js: JetStreamClient }> {
  const key = poolKey(natsUrl, token);
  let entry = pool.get(key);
  if (!entry) {
    entry = { ref: 0, promise: connectNatsJetStream(natsUrl, token) };
    pool.set(key, entry);
  }
  entry.ref += 1;
  try {
    return await entry.promise;
  } catch (err) {
    entry.ref -= 1;
    if (entry.ref <= 0) pool.delete(key);
    throw err;
  }
}

export async function releaseGlobalNatsJetStream(
  natsUrl: string,
  token: string
): Promise<void> {
  const key = poolKey(natsUrl, token);
  const entry = pool.get(key);
  if (!entry) return;
  entry.ref -= 1;
  if (entry.ref > 0) return;
  pool.delete(key);
  try {
    const { nc } = await entry.promise;
    await drainNats(nc);
  } catch {
    /* ignore */
  }
}

// --- jetstream: ordered consumer for one symbol ------------------------------

/**
 * Normalizes stream payloads to OHLC: full klines, or tickers like
 * `{ ts, symbol, tvId, price, volume }`.
 */
function payloadToKline(raw: Record<string, unknown>): PriceKlinePayload {
  const tsRaw = raw.ts ?? raw.timestamp ?? raw.time;
  const n = Number(tsRaw);
  const timeMs = Number.isFinite(n) ? (n > 1e12 ? n : n * 1000) : Date.now();

  const close = Number(raw.close ?? raw.price ?? raw.last ?? raw.c) || 0;
  const open = Number(raw.open ?? raw.o) || close;
  const high = Number(raw.high ?? raw.h) || close;
  const low = Number(raw.low ?? raw.l) || close;

  return {
    timestamp: timeMs,
    open,
    high,
    low,
    close,
    volume: raw.volume != null ? Number(raw.volume) : undefined,
  };
}

/**
 * Same pattern as server-side samples: `jetstreamManager` → `consumers.add` → `get` → `consume({ callback })`.
 */
async function runJsmPriceConsumer(
  nc: NatsConnection,
  js: JetStreamClient,
  streamName: string,
  symbol: string,
  onUpdate: (data: PriceKlinePayload) => void
): Promise<NatsLiveHandle> {
  const filterSubject = priceSubject(symbol);
  const jsm = await jetstreamManager(nc);
  const ci = await jsm.consumers.add(streamName, {
    filter_subjects: [filterSubject],
    ack_policy: AckPolicy.None,
    deliver_policy: DeliverPolicy.New,
  });
  const consumer = await js.consumers.get(streamName, ci.name);
  let cancelled = false;

  const messages = await consumer.consume({
    callback: (m) => {
      if (cancelled) return;
      try {
        const raw = m.json() as Record<string, unknown>;
        const kline = payloadToKline(raw);
        if (import.meta.env.DEV) {
          console.log("[TraderVerse NATS]", { subject: filterSubject, symbol, stream: streamName, ...kline });
        }
        onUpdate(kline);
      } catch {
        /* bad payload */
      }
    },
  });

  return {
    close: async () => {
      cancelled = true;
      try {
        await messages.close();
      } catch {
        /* ignore */
      }
      try {
        await consumer.delete();
      } catch {
        /* ignore */
      }
    },
  };
}

/** Low-level: same as {@link subscribeSymbol} but uses an existing pooled connection. */
export async function consumePriceUpdates(
  nc: NatsConnection,
  js: JetStreamClient,
  streamName: string,
  stockId: string,
  onKline: (data: PriceKlinePayload) => void
): Promise<NatsLiveHandle> {
  return runJsmPriceConsumer(nc, js, streamName, stockId, onKline);
}

/**
 * Subscribe to live price/kline updates for a **symbol** (ticker / stock id).
 * Resolves auth from props / localStorage / dev JWT / apiKey, uses the shared connection pool,
 * and returns a handle whose `close()` tears down the consumer and releases the pool ref.
 */
export async function subscribeSymbol(
  symbol: string,
  onUpdate: (data: PriceKlinePayload) => void,
  options: SubscribeSymbolOptions = {}
): Promise<NatsLiveHandle> {
  const natsUrl = options.natsUrl ?? DEFAULT_NATS_URL;
  const token = resolveNatsAuthToken({ natsToken: options.natsToken, apiKey: options.apiKey });
  if (!token) {
    throw new Error(
      "subscribeSymbol: missing token (set natsToken, apiKey, or localStorage auth_token / token)"
    );
  }
  const streamName = options.streamName ?? DEFAULT_STREAM_NAME;

  const { nc, js } = await acquireGlobalNatsJetStream(natsUrl, token);
  const inner = await runJsmPriceConsumer(nc, js, streamName, symbol, onUpdate);

  return {
    close: async () => {
      await inner.close();
      await releaseGlobalNatsJetStream(natsUrl, token);
    },
  };
}
